import { useCallback, useEffect, useState } from "react";
import {
  createManufacturingOverheadBucket,
  deleteManufacturingOverheadBucket,
  listManufacturingOverheadBuckets,
  updateManufacturingOverheadBucket,
} from "@/lib/manufacturingClient";
import type {
  MfgBucketType,
  MfgOverheadBucket,
  MfgOverheadBucketTreeNode,
} from "@/lib/manufacturing/overheadTypes";

export type OverheadBucketDraft = {
  name: string;
  type_key: string;
  parent_bucket_id: string;
  category: string;
  notes: string;
  is_aggregate: boolean;
  tracks_balance: boolean;
  balance_start_usd: string;
  balance_as_of: string;
  principal_monthly_usd: string;
};

export const EMPTY_OVERHEAD_BUCKET_DRAFT: OverheadBucketDraft = {
  name: "",
  type_key: "custom",
  parent_bucket_id: "",
  category: "",
  notes: "",
  is_aggregate: false,
  tracks_balance: false,
  balance_start_usd: "",
  balance_as_of: "",
  principal_monthly_usd: "",
};

function moneyOrNull(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

export function useManufacturingOverheadBuckets(enabled: boolean, navTick: number) {
  const [bucketTypes, setBucketTypes] = useState<MfgBucketType[]>([]);
  const [tree, setTree] = useState<MfgOverheadBucketTreeNode[]>([]);
  const [flatBuckets, setFlatBuckets] = useState<MfgOverheadBucket[]>([]);
  const [listBusy, setListBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [createDraft, setCreateDraft] = useState<OverheadBucketDraft>(EMPTY_OVERHEAD_BUCKET_DRAFT);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<OverheadBucketDraft>(EMPTY_OVERHEAD_BUCKET_DRAFT);

  const reload = useCallback(async () => {
    if (!enabled) return;
    setListBusy(true);
    setLocalError(null);
    const r = await listManufacturingOverheadBuckets();
    setListBusy(false);
    if (!r.ok) {
      setLocalError(r.error);
      return;
    }
    setBucketTypes(r.data.bucket_types);
    setTree(r.data.tree);
    setFlatBuckets(r.data.buckets);
  }, [enabled]);

  useEffect(() => {
    void reload();
  }, [reload, navTick]);

  function startEdit(bucket: MfgOverheadBucket) {
    setEditingId(bucket.id);
    setEditDraft({
      name: bucket.name,
      type_key: bucket.bucket_type_key,
      parent_bucket_id: bucket.parent_bucket_id ?? "",
      category: bucket.category,
      notes: bucket.notes,
      is_aggregate: bucket.is_aggregate,
      tracks_balance: bucket.tracks_balance,
      balance_start_usd: bucket.balance_start_usd == null ? "" : String(bucket.balance_start_usd),
      balance_as_of: bucket.balance_as_of ? bucket.balance_as_of.slice(0, 7) : "",
      principal_monthly_usd: bucket.principal_monthly_usd == null ? "" : String(bucket.principal_monthly_usd),
    });
    setShowCreate(false);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditDraft(EMPTY_OVERHEAD_BUCKET_DRAFT);
  }

  async function submitCreate() {
    const name = createDraft.name.trim();
    if (!name) {
      setLocalError("Enter a bucket name.");
      return;
    }
    setActionBusy(true);
    setLocalError(null);
    const r = await createManufacturingOverheadBucket({
      name,
      type_key: createDraft.type_key,
      parent_bucket_id: createDraft.is_aggregate ? null : createDraft.parent_bucket_id || null,
      category: createDraft.category.trim(),
      notes: createDraft.notes.trim(),
      is_aggregate: createDraft.is_aggregate,
      tracks_balance: createDraft.is_aggregate ? false : createDraft.tracks_balance,
      balance_start_usd: moneyOrNull(createDraft.balance_start_usd),
      balance_as_of: createDraft.balance_as_of,
      principal_monthly_usd: moneyOrNull(createDraft.principal_monthly_usd),
    });
    setActionBusy(false);
    if (!r.ok) {
      setLocalError(r.error);
      return;
    }
    setCreateDraft(EMPTY_OVERHEAD_BUCKET_DRAFT);
    setShowCreate(false);
    await reload();
  }

  async function submitEdit() {
    if (!editingId) return;
    const name = editDraft.name.trim();
    if (!name) {
      setLocalError("Enter a bucket name.");
      return;
    }
    setActionBusy(true);
    setLocalError(null);
    const type = bucketTypes.find((t) => t.type_key === editDraft.type_key);
    const r = await updateManufacturingOverheadBucket({
      bucket_id: editingId,
      name,
      bucket_type_id: type?.id,
      parent_bucket_id: editDraft.is_aggregate ? null : editDraft.parent_bucket_id || null,
      category: editDraft.category.trim(),
      notes: editDraft.notes.trim(),
      is_aggregate: editDraft.is_aggregate,
      tracks_balance: editDraft.is_aggregate ? false : editDraft.tracks_balance,
      balance_start_usd: moneyOrNull(editDraft.balance_start_usd),
      balance_as_of: editDraft.balance_as_of,
      principal_monthly_usd: moneyOrNull(editDraft.principal_monthly_usd),
    });
    setActionBusy(false);
    if (!r.ok) {
      setLocalError(r.error);
      return;
    }
    cancelEdit();
    await reload();
  }

  async function moveBucket(bucketId: string, parentId: string | null) {
    setActionBusy(true);
    setLocalError(null);
    const r = await updateManufacturingOverheadBucket({
      bucket_id: bucketId,
      parent_bucket_id: parentId,
    });
    setActionBusy(false);
    if (!r.ok) {
      setLocalError(r.error);
      return;
    }
    if (editingId === bucketId) cancelEdit();
    await reload();
  }

  async function deleteBucket(bucketId: string) {
    setActionBusy(true);
    setLocalError(null);
    const r = await deleteManufacturingOverheadBucket({ bucket_id: bucketId });
    setActionBusy(false);
    if (!r.ok) {
      setLocalError(r.error);
      return;
    }
    if (editingId === bucketId) cancelEdit();
    await reload();
  }

  async function toggleActive(bucket: MfgOverheadBucket) {
    setActionBusy(true);
    setLocalError(null);
    const r = await updateManufacturingOverheadBucket({
      bucket_id: bucket.id,
      is_active: !bucket.is_active,
    });
    setActionBusy(false);
    if (!r.ok) {
      setLocalError(r.error);
      return;
    }
    await reload();
  }

  async function retireBucket(bucket: MfgOverheadBucket, plantId: string, restore: boolean) {
    if (!restore && !plantId) {
      setLocalError("Select a plant and a consumption day before retiring a bucket.");
      return;
    }
    setActionBusy(true);
    setLocalError(null);
    const r = await updateManufacturingOverheadBucket({
      bucket_id: bucket.id,
      retire: !restore,
      plant_id: plantId,
    });
    setActionBusy(false);
    if (!r.ok) {
      setLocalError(r.error);
      return;
    }
    await reload();
  }

  return {
    bucketTypes,
    tree,
    flatBuckets,
    listBusy,
    actionBusy,
    localError,
    setLocalError,
    createDraft,
    setCreateDraft,
    showCreate,
    setShowCreate,
    editingId,
    editDraft,
    setEditDraft,
    startEdit,
    cancelEdit,
    submitCreate,
    submitEdit,
    moveBucket,
    deleteBucket,
    toggleActive,
    retireBucket,
    reload,
  };
}

export type ManufacturingOverheadBucketsState = ReturnType<typeof useManufacturingOverheadBuckets>;
